import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  PenTool, Highlighter, Square, Circle, ArrowRight, Minus, 
  Type, Eraser, RotateCcw, RotateCw, Download, Copy, Trash2, 
  Maximize2, Minimize2, PictureInPicture, X, Check, Palette, 
  Layers, Move, ZoomIn, ZoomOut, Grid, HelpCircle
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

export interface WhiteboardProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  initialMode?: 'fullscreen' | 'modal' | 'pip';
  onSnapshot?: (dataUrl: string) => void;
}

type ToolType = 'pen' | 'highlighter' | 'line' | 'arrow' | 'rectangle' | 'circle' | 'text' | 'eraser';
type BackgroundType = 'white' | 'dark' | 'grid' | 'legalpad';

interface Point {
  x: number;
  y: number;
  pressure?: number;
}

export const ProfessionalLegalWhiteboard: React.FC<WhiteboardProps> = ({
  isOpen,
  onClose,
  title = '🏛️ Supreme & High Court Legal Strategy Whiteboard',
  initialMode = 'modal',
  onSnapshot
}) => {
  const { colors } = useTheme();

  // WINDOW-IN-WINDOW (PiP) & DISPLAY MODES
  const [windowMode, setWindowMode] = useState<'fullscreen' | 'modal' | 'pip'>(initialMode);
  const [pipPosition, setPipPosition] = useState<{ x: number; y: number }>({ x: 30, y: 30 });
  const [isDraggingPip, setIsDraggingPip] = useState(false);
  const dragStartRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number }>({ startX: 0, startY: 0, initialX: 0, initialY: 0 });

  // DRAWING TOOLS & ATTRIBUTES
  const [activeTool, setActiveTool] = useState<ToolType>('pen');
  const [color, setColor] = useState<string>('#0284c7');
  const [strokeWidth, setStrokeWidth] = useState<number>(3);
  const [backgroundType, setBackgroundType] = useState<BackgroundType>('white');
  const [isCopied, setIsCopied] = useState(false);

  // TEXT TOOL STATE
  const [textInput, setTextInput] = useState<{ text: string; x: number; y: number; isEditing: boolean } | null>(null);

  // CANVAS REFS & DRAWING ENGINE
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isDrawingRef = useRef<boolean>(false);
  const pointsRef = useRef<Point[]>([]);
  const startPointRef = useRef<Point | null>(null);
  const previewSnapshotRef = useRef<ImageData | null>(null);

  // UNDO / REDO HISTORY STACKS
  const historyStackRef = useRef<ImageData[]>([]);
  const redoStackRef = useRef<ImageData[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // PRESET PALETTE
  const PALETTE = [
    { label: 'High Court Navy', hex: '#0284c7' },
    { label: 'Supreme Emerald', hex: '#16a34a' },
    { label: 'Judicial Gold', hex: '#ca8a04' },
    { label: 'Crimson Law', hex: '#dc2626' },
    { label: 'Statute Purple', hex: '#7c3aed' },
    { label: 'Ink Black', hex: '#0f172a' },
    { label: 'Highlighter Amber', hex: '#facc15' },
    { label: 'Pure White', hex: '#ffffff' },
  ];

  // 1. RESIZE & HIGH-DPI RETINA SCALING
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    // Save existing canvas image if present
    const ctx = canvas.getContext('2d');
    let tempImage: ImageData | null = null;
    if (ctx && canvas.width > 0 && canvas.height > 0) {
      try {
        tempImage = ctx.getImageData(0, 0, canvas.width, canvas.height);
      } catch (e) {
        // ignore
      }
    }

    // Set buffer resolution to match device pixel ratio for crystal clear rendering
    canvas.width = Math.max(300, rect.width * dpr);
    canvas.height = Math.max(200, rect.height * dpr);

    // Set visual display size
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    if (ctx) {
      ctx.scale(dpr, dpr);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Restore or draw initial background
      if (tempImage) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = tempImage.width;
        tempCanvas.height = tempImage.height;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx?.putImageData(tempImage, 0, 0);
        ctx.drawImage(tempCanvas, 0, 0, rect.width, rect.height);
      } else {
        drawBackground(ctx, rect.width, rect.height, backgroundType);
        saveHistoryState();
      }
    }
  }, [backgroundType]);

  // 2. BACKGROUND DRAWING (White, Dark Slate, Grid Paper, Yellow Legal Pad)
  const drawBackground = (ctx: CanvasRenderingContext2D, width: number, height: number, type: BackgroundType) => {
    ctx.save();
    if (type === 'white') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
    } else if (type === 'dark') {
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, width, height);
    } else if (type === 'grid') {
      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(0, 0, width, height);
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 1;
      const gridSize = 25;
      for (let x = 0; x < width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let y = 0; y < height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
    } else if (type === 'legalpad') {
      ctx.fillStyle = '#fef9c3'; // Light yellow legal pad
      ctx.fillRect(0, 0, width, height);
      // Red legal margin line
      ctx.strokeStyle = '#f87171';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(60, 0);
      ctx.lineTo(60, height);
      ctx.stroke();
      // Blue horizontal ruled lines
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 1;
      for (let y = 40; y < height; y += 28) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
    }
    ctx.restore();
  };

  // Trigger resize on open and mode switch
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        resizeCanvas();
      }, 50);
      window.addEventListener('resize', resizeCanvas);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', resizeCanvas);
      };
    }
  }, [isOpen, windowMode, resizeCanvas]);

  // 3. HISTORY STATE MANAGEMENT
  const saveHistoryState = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    try {
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      historyStackRef.current.push(imgData);
      if (historyStackRef.current.length > 25) {
        historyStackRef.current.shift();
      }
      redoStackRef.current = [];
      setCanUndo(historyStackRef.current.length > 1);
      setCanRedo(false);
    } catch (e) {
      console.warn('Whiteboard history snapshot note:', e);
    }
  };

  const handleUndo = () => {
    const canvas = canvasRef.current;
    if (!canvas || historyStackRef.current.length <= 1) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const current = historyStackRef.current.pop();
    if (current) {
      redoStackRef.current.push(current);
    }
    const previous = historyStackRef.current[historyStackRef.current.length - 1];
    if (previous) {
      ctx.putImageData(previous, 0, 0);
    }
    setCanUndo(historyStackRef.current.length > 1);
    setCanRedo(redoStackRef.current.length > 0);
  };

  const handleRedo = () => {
    const canvas = canvasRef.current;
    if (!canvas || redoStackRef.current.length === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const next = redoStackRef.current.pop();
    if (next) {
      historyStackRef.current.push(next);
      ctx.putImageData(next, 0, 0);
    }
    setCanUndo(historyStackRef.current.length > 1);
    setCanRedo(redoStackRef.current.length > 0);
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear the entire legal whiteboard?')) {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const rect = container.getBoundingClientRect();
      drawBackground(ctx, rect.width, rect.height, backgroundType);
      saveHistoryState();
    }
  };

  // 4. COORDINATE CALCULATION HELPER (ACCURATE TOUCH & MOUSE)
  const getCanvasCoords = (e: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX;
    const clientY = e.clientY;
    const pressure = e.pressure && e.pressure > 0 ? e.pressure : 0.5;

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
      pressure
    };
  };

  // 5. DRAWING POINTER HANDLERS (TOUCH, STYLUS & MOUSE)
  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pt = getCanvasCoords(e);

    if (activeTool === 'text') {
      setTextInput({ text: '', x: pt.x, y: pt.y, isEditing: true });
      return;
    }

    isDrawingRef.current = true;
    startPointRef.current = pt;
    pointsRef.current = [pt];

    try {
      previewSnapshotRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
    } catch (err) {
      // ignore
    }

    if (activeTool === 'pen' || activeTool === 'highlighter' || activeTool === 'eraser') {
      ctx.beginPath();
      ctx.moveTo(pt.x, pt.y);
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pt = getCanvasCoords(e);
    const startPt = startPointRef.current;

    // SHAPES (Line, Arrow, Rectangle, Circle) with Live Drag Preview
    if (['line', 'arrow', 'rectangle', 'circle'].includes(activeTool) && startPt && previewSnapshotRef.current) {
      ctx.putImageData(previewSnapshotRef.current, 0, 0);

      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (activeTool === 'line') {
        ctx.beginPath();
        ctx.moveTo(startPt.x, startPt.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.stroke();
      } else if (activeTool === 'arrow') {
        ctx.beginPath();
        ctx.moveTo(startPt.x, startPt.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.stroke();

        const angle = Math.atan2(pt.y - startPt.y, pt.x - startPt.x);
        const headlen = 14 + strokeWidth;
        ctx.beginPath();
        ctx.moveTo(pt.x, pt.y);
        ctx.lineTo(pt.x - headlen * Math.cos(angle - Math.PI / 6), pt.y - headlen * Math.sin(angle - Math.PI / 6));
        ctx.moveTo(pt.x, pt.y);
        ctx.lineTo(pt.x - headlen * Math.cos(angle + Math.PI / 6), pt.y - headlen * Math.sin(angle + Math.PI / 6));
        ctx.stroke();
      } else if (activeTool === 'rectangle') {
        const x = Math.min(startPt.x, pt.x);
        const y = Math.min(startPt.y, pt.y);
        const w = Math.abs(pt.x - startPt.x);
        const h = Math.abs(pt.y - startPt.y);
        ctx.beginPath();
        ctx.rect(x, y, w, h);
        ctx.stroke();
      } else if (activeTool === 'circle') {
        const radiusX = Math.abs(pt.x - startPt.x) / 2;
        const radiusY = Math.abs(pt.y - startPt.y) / 2;
        const centerX = Math.min(startPt.x, pt.x) + radiusX;
        const centerY = Math.min(startPt.y, pt.y) + radiusY;
        ctx.beginPath();
        ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
        ctx.stroke();
      }
      ctx.restore();
      return;
    }

    // SMOOTH FREEHAND PEN / HIGHLIGHTER / ERASER WITH QUADRATIC BEZIER INTERPOLATION
    pointsRef.current.push(pt);
    const points = pointsRef.current;

    ctx.save();
    if (activeTool === 'eraser') {
      ctx.strokeStyle = backgroundType === 'dark' ? '#0f172a' : backgroundType === 'legalpad' ? '#fef9c3' : '#ffffff';
      ctx.lineWidth = strokeWidth * 6;
      ctx.globalAlpha = 1.0;
    } else if (activeTool === 'highlighter') {
      ctx.strokeStyle = color;
      ctx.lineWidth = strokeWidth * 4;
      ctx.globalAlpha = 0.35;
      ctx.lineCap = 'square';
    } else {
      ctx.strokeStyle = color;
      const dynamicWidth = pt.pressure ? Math.max(1, strokeWidth * (pt.pressure * 1.5)) : strokeWidth;
      ctx.lineWidth = dynamicWidth;
      ctx.globalAlpha = 1.0;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }

    if (points.length > 2) {
      const p1 = points[points.length - 2];
      const p2 = points[points.length - 1];
      const midPoint = {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2
      };

      ctx.beginPath();
      ctx.moveTo(points[points.length - 3].x, points[points.length - 3].y);
      ctx.quadraticCurveTo(p1.x, p1.y, midPoint.x, midPoint.y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
    }
    ctx.restore();
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    pointsRef.current = [];
    startPointRef.current = null;
    previewSnapshotRef.current = null;

    saveHistoryState();
  };

  // 6. INSERT TEXT ANNOTATION
  const handleCommitText = () => {
    if (!textInput || !textInput.text.trim()) {
      setTextInput(null);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.save();
    ctx.font = `bold ${Math.max(14, strokeWidth * 5)}px "Outfit", system-ui, sans-serif`;
    ctx.fillStyle = color;
    ctx.fillText(textInput.text, textInput.x, textInput.y + 14);
    ctx.restore();

    setTextInput(null);
    saveHistoryState();
  };

  // 7. EXPORT & DOWNLOAD SNAPSHOT
  const handleDownloadSnapshot = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvas.width;
    exportCanvas.height = canvas.height;
    const expCtx = exportCanvas.getContext('2d');
    if (!expCtx) return;

    expCtx.drawImage(canvas, 0, 0);

    expCtx.save();
    expCtx.fillStyle = 'rgba(15, 23, 42, 0.7)';
    expCtx.font = 'bold 16px "Outfit", sans-serif';
    expCtx.fillText(`⚖️ ELum Law Associates • Judicial Strategy Diagram • ${new Date().toLocaleString()}`, 20, exportCanvas.height - 20);
    expCtx.restore();

    const dataUrl = exportCanvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `ELum_Legal_Whiteboard_${Date.now()}.png`;
    link.href = dataUrl;
    link.click();

    if (onSnapshot) {
      onSnapshot(dataUrl);
    }
  };

  // 8. COPY IMAGE TO CLIPBOARD
  const handleCopySnapshot = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !navigator.clipboard) return;

    try {
      canvas.toBlob(async (blob) => {
        if (blob) {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
          ]);
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        }
      });
    } catch (e) {
      console.warn('Clipboard copy error:', e);
    }
  };

  // 9. WINDOW-IN-WINDOW (PiP) DRAG HANDLERS
  const handlePipMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (windowMode !== 'pip') return;
    setIsDraggingPip(true);
    dragStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: pipPosition.x,
      initialY: pipPosition.y
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingPip) return;
      const dx = e.clientX - dragStartRef.current.startX;
      const dy = e.clientY - dragStartRef.current.startY;
      setPipPosition({
        x: Math.max(10, Math.min(window.innerWidth - 380, dragStartRef.current.initialX - dx)),
        y: Math.max(10, Math.min(window.innerHeight - 300, dragStartRef.current.initialY - dy))
      });
    };

    const handleMouseUp = () => {
      setIsDraggingPip(false);
    };

    if (isDraggingPip) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingPip]);

  if (!isOpen) return null;

  // -------------------------------------------------------------
  // RENDER: WINDOW-IN-WINDOW (PiP) FLOATING MINI WHITEBOARD
  // -------------------------------------------------------------
  if (windowMode === 'pip') {
    return (
      <div 
        style={{
          position: 'fixed',
          bottom: `${pipPosition.y}px`,
          right: `${pipPosition.x}px`,
          width: '460px',
          height: '340px',
          backgroundColor: '#0f172a',
          borderRadius: '16px',
          boxShadow: '0 25px 60px rgba(0,0,0,0.85)',
          border: `2px solid ${colors.goldPrimary}`,
          zIndex: 999999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          fontFamily: '"Outfit", system-ui, sans-serif'
        }}
      >
        {/* PiP Header (Draggable) */}
        <div 
          onMouseDown={handlePipMouseDown}
          style={{
            backgroundColor: '#1e293b',
            padding: '0.5rem 0.85rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'move',
            userSelect: 'none',
            borderBottom: '1px solid #334155'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: colors.goldPrimary, fontWeight: '800', fontSize: '0.82rem' }}>
            <PenTool size={14} />
            <span>Legal Whiteboard (PiP)</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <button 
              onClick={() => setWindowMode('modal')}
              title="Expand to Standard View"
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px' }}
            >
              <Minimize2 size={15} />
            </button>
            <button 
              onClick={() => setWindowMode('fullscreen')}
              title="Expand to Fullscreen"
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px' }}
            >
              <Maximize2 size={15} />
            </button>
            <button 
              onClick={onClose}
              title="Close Whiteboard"
              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Mini Toolbar */}
        <div style={{ padding: '0.35rem 0.6rem', backgroundColor: '#090d16', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #1e293b' }}>
          <div style={{ display: 'flex', gap: '0.3rem' }}>
            <button onClick={() => setActiveTool('pen')} style={{ padding: '0.25rem 0.4rem', borderRadius: '4px', border: 'none', backgroundColor: activeTool === 'pen' ? '#0284c7' : '#1e293b', color: 'white', cursor: 'pointer', fontSize: '0.75rem' }}><PenTool size={12} /></button>
            <button onClick={() => setActiveTool('highlighter')} style={{ padding: '0.25rem 0.4rem', borderRadius: '4px', border: 'none', backgroundColor: activeTool === 'highlighter' ? '#ca8a04' : '#1e293b', color: 'white', cursor: 'pointer', fontSize: '0.75rem' }}><Highlighter size={12} /></button>
            <button onClick={() => setActiveTool('eraser')} style={{ padding: '0.25rem 0.4rem', borderRadius: '4px', border: 'none', backgroundColor: activeTool === 'eraser' ? '#dc2626' : '#1e293b', color: 'white', cursor: 'pointer', fontSize: '0.75rem' }}><Eraser size={12} /></button>
          </div>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            {['#0284c7', '#dc2626', '#16a34a', '#ca8a04', '#0f172a'].map(c => (
              <button key={c} onClick={() => setColor(c)} style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: c, border: color === c ? '2px solid white' : 'none', cursor: 'pointer' }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.3rem' }}>
            <button onClick={handleUndo} disabled={!canUndo} style={{ background: 'none', border: 'none', color: canUndo ? 'white' : '#475569', cursor: canUndo ? 'pointer' : 'default' }}><RotateCcw size={13} /></button>
            <button onClick={handleClearAll} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={13} /></button>
          </div>
        </div>

        {/* Mini Canvas Container */}
        <div ref={containerRef} style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <canvas 
            ref={canvasRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{ width: '100%', height: '100%', touchAction: 'none', cursor: activeTool === 'eraser' ? 'crosshair' : 'default' }}
          />
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // RENDER: FULLSCREEN OR CENTER MODAL LEGAL WHITEBOARD
  // -------------------------------------------------------------
  const isFullscreen = windowMode === 'fullscreen';

  return (
    <div 
      style={{
        position: 'fixed',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        right: isFullscreen ? 0 : 'auto',
        bottom: isFullscreen ? 0 : 'auto',
        ...(isFullscreen ? {} : {
          top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem'
        }),
        backgroundColor: isFullscreen ? '#090d16' : 'rgba(0,0,0,0.85)',
        zIndex: 999999,
        fontFamily: '"Outfit", system-ui, sans-serif'
      }}
    >
      <div 
        style={{
          width: isFullscreen ? '100vw' : '100%',
          maxWidth: isFullscreen ? '100vw' : '1100px',
          height: isFullscreen ? '100vh' : '82vh',
          backgroundColor: '#0f172a',
          borderRadius: isFullscreen ? '0' : '20px',
          border: isFullscreen ? 'none' : `2px solid ${colors.goldPrimary}`,
          boxShadow: isFullscreen ? 'none' : '0 30px 80px rgba(0,0,0,0.9)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* TOP COMMAND HEADER */}
        <div 
          style={{
            backgroundColor: '#1e293b',
            padding: '0.75rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #334155',
            color: 'white'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: 'rgba(236,201,75,0.15)', color: colors.goldPrimary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PenTool size={20} />
            </div>
            <div>
              <div style={{ fontWeight: '900', fontSize: '1rem', color: colors.goldPrimary }}>
                {title}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                Touch & Stylus Precision • Vector Bézier Smoothing • Window-in-Window Multitasking
              </div>
            </div>
          </div>

          {/* Window Mode Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <button 
              onClick={() => setWindowMode('pip')}
              title="Window-in-Window (PiP Floating Mode)"
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', backgroundColor: '#0f172a', color: colors.goldPrimary, border: '1px solid #334155', borderRadius: '8px', padding: '0.4rem 0.75rem', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
            >
              <PictureInPicture size={14} /> PiP Mode
            </button>

            <button 
              onClick={() => setWindowMode(isFullscreen ? 'modal' : 'fullscreen')}
              title={isFullscreen ? "Restore View" : "Fullscreen View"}
              style={{ backgroundColor: '#0f172a', color: 'white', border: '1px solid #334155', borderRadius: '8px', padding: '0.4rem 0.65rem', cursor: 'pointer' }}
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>

            <button 
              onClick={onClose}
              title="Close Whiteboard"
              style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '8px', padding: '0.4rem 0.65rem', cursor: 'pointer' }}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* COMPREHENSIVE LEGAL TOOLBAR */}
        <div 
          style={{
            backgroundColor: '#090d16',
            padding: '0.6rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #1e293b',
            flexWrap: 'wrap',
            gap: '0.75rem'
          }}
        >
          {/* 1. Tool Selectors */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', backgroundColor: '#1e293b', padding: '0.25rem', borderRadius: '10px' }}>
            <button
              onClick={() => setActiveTool('pen')}
              title="Precision Fountain Pen"
              style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: 'none', backgroundColor: activeTool === 'pen' ? '#0284c7' : 'transparent', color: 'white', fontWeight: 'bold', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
            >
              <PenTool size={15} /> Pen
            </button>

            <button
              onClick={() => setActiveTool('highlighter')}
              title="Legal Highlighter Marker"
              style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: 'none', backgroundColor: activeTool === 'highlighter' ? '#ca8a04' : 'transparent', color: 'white', fontWeight: 'bold', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
            >
              <Highlighter size={15} /> Highlighter
            </button>

            <button
              onClick={() => setActiveTool('line')}
              title="Straight Line"
              style={{ padding: '0.4rem 0.5rem', borderRadius: '6px', border: 'none', backgroundColor: activeTool === 'line' ? '#0284c7' : 'transparent', color: 'white', cursor: 'pointer' }}
            >
              <Minus size={15} />
            </button>

            <button
              onClick={() => setActiveTool('arrow')}
              title="Flowchart Arrow"
              style={{ padding: '0.4rem 0.5rem', borderRadius: '6px', border: 'none', backgroundColor: activeTool === 'arrow' ? '#0284c7' : 'transparent', color: 'white', cursor: 'pointer' }}
            >
              <ArrowRight size={15} />
            </button>

            <button
              onClick={() => setActiveTool('rectangle')}
              title="Case Law Citation Box"
              style={{ padding: '0.4rem 0.5rem', borderRadius: '6px', border: 'none', backgroundColor: activeTool === 'rectangle' ? '#0284c7' : 'transparent', color: 'white', cursor: 'pointer' }}
            >
              <Square size={15} />
            </button>

            <button
              onClick={() => setActiveTool('circle')}
              title="Judicial Seal / Circle"
              style={{ padding: '0.4rem 0.5rem', borderRadius: '6px', border: 'none', backgroundColor: activeTool === 'circle' ? '#0284c7' : 'transparent', color: 'white', cursor: 'pointer' }}
            >
              <Circle size={15} />
            </button>

            <button
              onClick={() => setActiveTool('text')}
              title="Insert Legal Note / Citation"
              style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: 'none', backgroundColor: activeTool === 'text' ? '#7c3aed' : 'transparent', color: 'white', fontWeight: 'bold', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
            >
              <Type size={15} /> Text
            </button>

            <button
              onClick={() => setActiveTool('eraser')}
              title="Eraser"
              style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: 'none', backgroundColor: activeTool === 'eraser' ? '#dc2626' : 'transparent', color: 'white', fontWeight: 'bold', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
            >
              <Eraser size={15} /> Eraser
            </button>
          </div>

          {/* 2. Color Palette */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            {PALETTE.map((p) => (
              <button 
                key={p.hex}
                onClick={() => { setColor(p.hex); if (activeTool === 'eraser') setActiveTool('pen'); }}
                title={p.label}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: p.hex,
                  border: color === p.hex && activeTool !== 'eraser' ? '3px solid white' : '1px solid #334155',
                  cursor: 'pointer',
                  transform: color === p.hex ? 'scale(1.15)' : 'none',
                  transition: 'transform 0.1s'
                }}
              />
            ))}
          </div>

          {/* 3. Stroke Width Slider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#94a3b8', fontSize: '0.75rem' }}>
            <span>Size:</span>
            <input 
              type="range" 
              min={1} 
              max={16} 
              value={strokeWidth} 
              onChange={(e) => setStrokeWidth(Number(e.target.value))}
              style={{ width: '70px', accentColor: '#0284c7', cursor: 'pointer' }}
            />
            <span style={{ color: 'white', fontWeight: 'bold', minWidth: '18px' }}>{strokeWidth}px</span>
          </div>

          {/* 4. Canvas Background Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <button 
              onClick={() => setBackgroundType('white')}
              title="Clean White Canvas"
              style={{ padding: '0.3rem 0.5rem', borderRadius: '6px', border: backgroundType === 'white' ? '1.5px solid #0284c7' : '1px solid #334155', backgroundColor: '#ffffff', color: 'black', fontSize: '0.72rem', fontWeight: '800', cursor: 'pointer' }}
            >
              Plain
            </button>
            <button 
              onClick={() => setBackgroundType('dark')}
              title="Dark Judicial Slate Canvas"
              style={{ padding: '0.3rem 0.5rem', borderRadius: '6px', border: backgroundType === 'dark' ? '1.5px solid #0284c7' : '1px solid #334155', backgroundColor: '#0f172a', color: 'white', fontSize: '0.72rem', fontWeight: '800', cursor: 'pointer' }}
            >
              Dark
            </button>
            <button 
              onClick={() => setBackgroundType('grid')}
              title="Courtroom Evidence Grid"
              style={{ padding: '0.3rem 0.5rem', borderRadius: '6px', border: backgroundType === 'grid' ? '1.5px solid #0284c7' : '1px solid #334155', backgroundColor: '#f1f5f9', color: '#0f172a', fontSize: '0.72rem', fontWeight: '800', cursor: 'pointer' }}
            >
              Grid
            </button>
            <button 
              onClick={() => setBackgroundType('legalpad')}
              title="Yellow Legal Pad"
              style={{ padding: '0.3rem 0.5rem', borderRadius: '6px', border: backgroundType === 'legalpad' ? '1.5px solid #ca8a04' : '1px solid #334155', backgroundColor: '#fef9c3', color: '#713f12', fontSize: '0.72rem', fontWeight: '800', cursor: 'pointer' }}
            >
              Legal Pad
            </button>
          </div>

          {/* 5. History & Export Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <button 
              onClick={handleUndo} 
              disabled={!canUndo} 
              title="Undo (Ctrl+Z)"
              style={{ backgroundColor: '#1e293b', color: canUndo ? 'white' : '#475569', border: '1px solid #334155', borderRadius: '6px', padding: '0.35rem 0.55rem', cursor: canUndo ? 'pointer' : 'default' }}
            >
              <RotateCcw size={15} />
            </button>

            <button 
              onClick={handleRedo} 
              disabled={!canRedo} 
              title="Redo (Ctrl+Y)"
              style={{ backgroundColor: '#1e293b', color: canRedo ? 'white' : '#475569', border: '1px solid #334155', borderRadius: '6px', padding: '0.35rem 0.55rem', cursor: canRedo ? 'pointer' : 'default' }}
            >
              <RotateCw size={15} />
            </button>

            <button 
              onClick={handleClearAll} 
              title="Clear Board"
              style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '6px', padding: '0.35rem 0.6rem', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              <Trash2 size={14} /> Clear
            </button>

            <button 
              onClick={handleCopySnapshot} 
              title="Copy Whiteboard Image to Clipboard"
              style={{ backgroundColor: '#1e293b', color: isCopied ? '#22c55e' : 'white', border: '1px solid #334155', borderRadius: '6px', padding: '0.35rem 0.65rem', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
            >
              {isCopied ? <Check size={14} /> : <Copy size={14} />} {isCopied ? 'Copied' : 'Copy'}
            </button>

            <button 
              onClick={handleDownloadSnapshot} 
              title="Download High-Resolution PNG Diagram"
              style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', borderRadius: '6px', padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem', boxShadow: '0 2px 8px rgba(2,132,199,0.4)' }}
            >
              <Download size={14} /> Export Diagram
            </button>
          </div>
        </div>

        {/* MAIN CANVAS WORKSPACE */}
        <div 
          ref={containerRef}
          style={{
            flex: 1,
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            backgroundColor: backgroundType === 'dark' ? '#0f172a' : backgroundType === 'legalpad' ? '#fef9c3' : '#ffffff'
          }}
        >
          <canvas 
            ref={canvasRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              touchAction: 'none',
              cursor: activeTool === 'text' ? 'text' : activeTool === 'eraser' ? 'crosshair' : 'crosshair'
            }}
          />

          {/* INLINE TEXT ANNOTATION POPUP */}
          {textInput && textInput.isEditing && (
            <div 
              style={{
                position: 'absolute',
                left: `${textInput.x}px`,
                top: `${textInput.y}px`,
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                padding: '0.6rem',
                borderRadius: '8px',
                border: `2px solid ${colors.goldPrimary}`,
                boxShadow: '0 10px 25px rgba(0,0,0,0.6)',
                zIndex: 100,
                display: 'flex',
                gap: '0.4rem',
                alignItems: 'center'
              }}
            >
              <input 
                type="text"
                autoFocus
                placeholder="Type legal note, statute or citation..."
                value={textInput.text}
                onChange={(e) => setTextInput({ ...textInput, text: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCommitText();
                  if (e.key === 'Escape') setTextInput(null);
                }}
                style={{
                  backgroundColor: '#1e293b',
                  color: 'white',
                  border: '1px solid #475569',
                  borderRadius: '6px',
                  padding: '0.4rem 0.65rem',
                  fontSize: '0.85rem',
                  outline: 'none',
                  width: '280px'
                }}
              />
              <button 
                onClick={handleCommitText}
                style={{ backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', padding: '0.4rem 0.75rem', fontWeight: 'bold', fontSize: '0.8rem', cursor: 'pointer' }}
              >
                Insert
              </button>
              <button 
                onClick={() => setTextInput(null)}
                style={{ backgroundColor: '#475569', color: 'white', border: 'none', borderRadius: '6px', padding: '0.4rem 0.5rem', cursor: 'pointer' }}
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* Bottom Left Quick Help Pill */}
          <div 
            style={{
              position: 'absolute',
              bottom: '12px',
              left: '16px',
              backgroundColor: 'rgba(15, 23, 42, 0.75)',
              backdropFilter: 'blur(6px)',
              padding: '0.25rem 0.65rem',
              borderRadius: '20px',
              fontSize: '0.7rem',
              color: '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              pointerEvents: 'none',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            <span>✍️ Mouse & Stylus Touch Ready</span>
            <span>•</span>
            <span>High-DPI Retina Buffer</span>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProfessionalLegalWhiteboard;
